import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CONSENT_DENIED,
  CONSENT_GRANTED,
  getAnalyticsConsent,
  onAnalyticsConsentChange,
  onOpenCookiePreferences,
  setAnalyticsConsent,
} from '../../lib/analyticsConsent';
import { ensurePostHogStarted } from '../../lib/posthog';

/**
 * Aviso de cookies (doc. 04 §3-4). «Aceptar» y «Rechazar» con el mismo tamaño y visibilidad; cerrar sin elegir
 * = sin analítica. No es modal. Se reabre con «Preferencias de cookies» (pie) o en /cookies.
 * Solo se monta en el layout público (PizzeriaChrome), nunca en /admin ni en /pagar.
 */
export default function CookieConsentBanner() {
  const [consent, setConsent] = useState(() => getAnalyticsConsent());
  const [open, setOpen] = useState(() => getAnalyticsConsent() === null);
  const headingRef = useRef(null);
  const returnFocusRef = useRef(null);

  useEffect(() => {
    ensurePostHogStarted(); // solo carga PostHog si ya había «Aceptar»
  }, []);

  useEffect(() => onAnalyticsConsentChange((v) => setConsent(v)), []);

  useEffect(
    () =>
      onOpenCookiePreferences(() => {
        returnFocusRef.current = document.activeElement;
        setConsent(getAnalyticsConsent());
        setOpen(true);
        window.setTimeout(() => headingRef.current && headingRef.current.focus(), 0);
      }),
    [],
  );

  const choose = (value) => {
    setAnalyticsConsent(value);
    setOpen(false);
    const el = returnFocusRef.current;
    returnFocusRef.current = null;
    if (el && typeof el.focus === 'function' && document.contains(el)) window.setTimeout(() => el.focus(), 0);
  };

  if (!open) return null;

  const btn =
    'min-h-[44px] min-w-[120px] rounded-full px-6 text-sm font-bold border-2 border-[#2B1A12] transition-colors';

  return (
    <section
      id="cookie-consent"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-desc"
      className="ph-no-capture sticky bottom-0 z-[60] px-3 pb-3 sm:px-4 sm:pb-4 pointer-events-none"
      data-testid="cookie-consent"
    >
      <div className="pointer-events-auto max-w-3xl mx-auto bg-[#FFFDF8] text-[#2B1A12] border border-[#F1DFC8] rounded-2xl shadow-lg p-4 sm:p-5">
        <h2 id="cookie-consent-title" ref={headingRef} tabIndex={-1} className="font-heading font-bold text-lg focus:outline-none">
          Cookies y analítica
        </h2>
        <p id="cookie-consent-desc" className="mt-1 text-sm leading-relaxed">
          Usamos almacenamiento necesario para su carrito y, solo si acepta, <strong>PostHog</strong> para estadísticas
          de uso. No grabamos sesiones ni lo que escribe en los formularios. Este es un sitio de demostración.{' '}
          <Link to="/cookies" className="font-semibold underline underline-offset-2 hover:no-underline">
            Más información
          </Link>
        </p>
        {consent ? (
          <p className="mt-2 text-sm" aria-live="polite">
            Elección actual: <strong>{consent === CONSENT_GRANTED ? 'aceptada' : 'rechazada'}</strong>
          </p>
        ) : null}
        <div className="mt-4 grid grid-cols-2 gap-2 sm:flex sm:justify-end sm:gap-3">
          <button
            type="button"
            onClick={() => choose(CONSENT_GRANTED)}
            className={`${btn} bg-[#2B1A12] text-[#FFF6EA] hover:bg-[#4a3022]`}
            data-testid="cookie-accept"
          >
            Aceptar
          </button>
          <button
            type="button"
            onClick={() => choose(CONSENT_DENIED)}
            className={`${btn} bg-[#2B1A12] text-[#FFF6EA] hover:bg-[#4a3022]`}
            data-testid="cookie-reject"
          >
            Rechazar
          </button>
        </div>
      </div>
    </section>
  );
}
